import { useEffect, useState } from "react";

declare global {
  interface Window {
    grecaptcha?: {
      ready?: (callback: () => void) => void;
      execute?: (siteKey: string, options: { action: string }) => Promise<string>;
    };
    ___grecaptcha_cfg?: unknown;
  }
}

let recaptchaScriptPromise: Promise<void> | null = null;

function clearStaleRecaptchaState() {
  document
    .querySelectorAll<HTMLScriptElement>('script[src^="https://www.google.com/recaptcha/api.js"]')
    .forEach((script) => script.remove());
  delete window.grecaptcha;
  delete window.___grecaptcha_cfg;
}

function loadRecaptchaScript(siteKey: string) {
  if (typeof window === "undefined") return Promise.resolve();
  if (window.grecaptcha?.execute) return Promise.resolve();
  if (recaptchaScriptPromise) return recaptchaScriptPromise;

  recaptchaScriptPromise = new Promise((resolve, reject) => {
    clearStaleRecaptchaState();

    const script = document.createElement("script");
    script.src = `https://www.google.com/recaptcha/api.js?render=${encodeURIComponent(siteKey)}`;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () => {
      recaptchaScriptPromise = null;
      reject(new Error("Google reCAPTCHA script failed to load"));
    };
    document.head.appendChild(script);
  });

  return recaptchaScriptPromise;
}

function waitForRecaptchaReady(timeoutMs = 10000) {
  return new Promise<Required<NonNullable<Window["grecaptcha"]>>>((resolve, reject) => {
    const startedAt = Date.now();

    const check = () => {
      const grecaptcha = window.grecaptcha;
      if (grecaptcha?.ready) {
        grecaptcha.ready(() => {
          const readyApi = window.grecaptcha;
          if (readyApi?.execute) {
            resolve(readyApi as Required<NonNullable<Window["grecaptcha"]>>);
            return;
          }

          if (Date.now() - startedAt >= timeoutMs) {
            reject(new Error("Google reCAPTCHA v3 execute API was not ready"));
            return;
          }

          window.setTimeout(check, 100);
        });
        return;
      }

      if (Date.now() - startedAt >= timeoutMs) {
        reject(new Error("Google reCAPTCHA v3 ready API was not available"));
        return;
      }

      window.setTimeout(check, 100);
    };

    check();
  });
}

type GoogleRecaptchaProps = {
  enabled: boolean;
  siteKey?: string;
  resetSignal?: number;
  action?: string;
  onChange: (token: string) => void;
  onExpired?: () => void;
};

export default function GoogleRecaptcha({
  enabled,
  siteKey,
  resetSignal = 0,
  action = "form_submit",
  onChange,
  onExpired,
}: GoogleRecaptchaProps) {
  const [errorMessage, setErrorMessage] = useState("");
  const [isReady, setIsReady] = useState(false);

  useEffect(() => {
    if (!enabled || !siteKey) return;

    let cancelled = false;
    setErrorMessage("");
    setIsReady(false);
    onChange("");

    loadRecaptchaScript(siteKey)
      .then(() => waitForRecaptchaReady())
      .then((grecaptcha) => {
        if (cancelled) return;

        return grecaptcha.execute(siteKey, { action });
      })
      .then((token) => {
        if (cancelled || !token) return;
        onChange(token);
        setIsReady(true);
      })
      .catch(() => {
        if (cancelled) return;
        setErrorMessage("Could not verify Google reCAPTCHA v3. Check domain/key settings and reload the page.");
        onExpired?.();
      });

    return () => {
      cancelled = true;
    };
  }, [enabled, siteKey, action, resetSignal, onChange, onExpired]);

  if (!enabled) return null;

  if (!siteKey) {
    return (
      <p className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-semibold text-red-700">
        CAPTCHA is enabled but the public site key is missing.
      </p>
    );
  }

  if (errorMessage) {
    return (
      <p className="rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-semibold text-red-700">
        {errorMessage}
      </p>
    );
  }

  return (
    <p className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs font-semibold text-slate-500">
      {isReady ? (
        <>
          This site is protected by reCAPTCHA and the Google{" "}
          <a
            href="https://policies.google.com/privacy"
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 underline underline-offset-2"
          >
            Privacy Policy
          </a>{" "}
          and{" "}
          <a
            href="https://policies.google.com/terms"
            target="_blank"
            rel="noreferrer"
            className="text-blue-600 underline underline-offset-2"
          >
            Terms of Service
          </a>{" "}
          apply.
        </>
      ) : (
        "Preparing security verification..."
      )}
    </p>
  );
}
